import React, { memo, useCallback, useEffect } from 'react';
import { isCarrelloTypeIppica, isCarrelloTypeSport, isCarrelloTypeVirtual } from 'features/carrello/utils';
import { isGermanSite, isSnaiSite } from 'utility/constant';
import { selectIsActiveIppica, selectIsActiveVirtual } from 'features/configuration/selectors';
import { selectIsFullCart, selectIsOpenCarrello, selectTipo } from 'features/carrello/carrelloSelectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { CarrelloContent } from 'features/carrello/carrelloContent/CarrelloContent';
import { CarrelloHeader } from 'features/carrello/carrelloHeader';
import { CarrelloProps } from 'features/carrello';
import { CarrelloSheet } from 'features/carrello/carrelloSheet';
import { MovimentiTipoScommessaEnum } from 'types/swagger';
import classNames from 'classnames';
import { setTaxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import styles from './Carrello.module.scss';
import { useCarrelloBonus } from 'features/carrello/hooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';
import { useLazyGetCachedProfileQuery } from 'features/api/userSlice';
import useWindowSize from 'hooks/useWindowSize';

export const Carrello = memo(function Carrello({ taxes }: CarrelloProps) {
  const { session, isAuthenticated } = useInternalSession();
  const dispatch = useAppDispatch();
  const isFullCart = useTypedSelector(selectIsFullCart);
  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const tipo = useTypedSelector(selectTipo);
  const isActiveVirtual = useTypedSelector(selectIsActiveVirtual);
  const isActiveIppica = useTypedSelector(selectIsActiveIppica);
  useCarrelloBonus();

  const [labelBiglietto, labelScommesse] = useGetLabelsByKeys(['biglietto', 'scommesse']);

  const [getSessionCacheProfile] = useLazyGetCachedProfileQuery();

  const { isMobile } = useWindowSize();

  const getTaxes = useCallback(async () => {
    if (isGermanSite || isAuthenticated) {
      let taxAppliedMultiple = taxes?.taxAppliedMultiple;
      let taxAppliedSingle = taxes?.taxAppliedSingle;
      let taxAppliedSystem = taxes?.taxAppliedSystem;
      if (session && isAuthenticated) {
        const { tax_applied_single, tax_applied_multiple, tax_applied_system } =
          await getSessionCacheProfile().unwrap();

        taxAppliedMultiple = tax_applied_multiple !== '' ? Number(tax_applied_multiple) : taxes?.taxAppliedMultiple;
        taxAppliedSingle = tax_applied_single !== '' ? Number(tax_applied_single) : taxes?.taxAppliedSingle;
        taxAppliedSystem = tax_applied_system !== '' ? Number(tax_applied_system) : taxes?.taxAppliedSystem;
      }

      dispatch(
        setTaxes({
          taxAppliedMultiple: taxAppliedMultiple ?? 0,
          taxAppliedSingle: taxAppliedSingle ?? 0,
          taxAppliedSystem: taxAppliedSystem ?? 0,
        })
      );
    } else if (!isSnaiSite && !isGermanSite && !isAuthenticated) dispatch(setTaxes(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxes, isAuthenticated, getSessionCacheProfile, dispatch]);

  useEffect(() => {
    if (!isSnaiSite) getTaxes();
  }, [getTaxes]);

  return (
    <div
      className={classNames(styles.container, { [styles.visibile]: isOpen && isMobile, [styles.fullCart]: isFullCart })}
    >
      {isFullCart && <CarrelloHeader />}
      {isFullCart && isMobile && (
        <div id="sheet-mount-point" className={styles.sheet}>
          <CarrelloSheet />
        </div>
      )}
      <CarrelloContent
        type={MovimentiTipoScommessaEnum.Sport}
        isActive={isCarrelloTypeSport(tipo)}
        labelBiglietto={labelBiglietto}
        labelScommesse={labelScommesse}
      />
      {isActiveIppica && (
        <CarrelloContent
          labelBiglietto={labelBiglietto}
          labelScommesse={labelScommesse}
          isActive={isCarrelloTypeIppica(tipo)}
          type={MovimentiTipoScommessaEnum.Ippica}
        />
      )}
      {isActiveVirtual && (
        <CarrelloContent
          labelBiglietto={labelBiglietto}
          labelScommesse={labelScommesse}
          isActive={isCarrelloTypeVirtual(tipo)}
          type={MovimentiTipoScommessaEnum.Virtual}
        />
      )}
    </div>
  );
});